<template>
  <DetailHeader
    pretitle="Audience Manager"
    :title="audience ? audience.audience_name : 'Loading...'"
    :editable="false"
    :editMode="false"
    :editRoles="['aqfer_admin', 'aqfer_user', 'client_admin']"
    @deleteClick="onDeleteClick"
    @closeClick="onCloseClick"
  />

  <div class="container-fluid">
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>

    <div v-else-if="audience" class="row">
      <!-- Left Panel - Actions & Audience Info -->
      <div class="col-md-4">
        <!-- Actions Card -->
        <div class="card">
          <div class="card-body">
            <div class="d-grid gap-2">
              <button
                type="button"
                class="btn btn-primary"
                @click="onRunCount"
                :disabled="isLoadingCount"
              >
                <span v-if="isLoadingCount" class="spinner-border spinner-border-sm me-2" />
                <i v-else class="fa fa-calculator me-2" />
                Get Count
              </button>
              <button
                type="button"
                class="btn btn-outline-primary"
                @click="showHistogramModal"
              >
                <i class="fa fa-chart-pie me-2" />
                Composition
              </button>
              <button
                type="button"
                class="btn btn-outline-primary"
                @click="showTopIndexingModal"
              >
                <i class="fa fa-arrow-up-right-dots me-2" />
                Top Indexing
              </button>
              <button
                type="button"
                class="btn btn-outline-primary"
                @click="showExportModal"
              >
                <i class="fa fa-download me-2" />
                Export User IDs
              </button>
            </div>

            <!-- Count Result -->
            <div v-if="audienceCount !== null" class="alert alert-success mt-3 mb-0">
              <strong>Count:</strong> {{ formatNumber(audienceCount) }}
            </div>
          </div>
        </div>

        <!-- Audience Details Card -->
        <div class="card mt-4">
          <div class="card-header">
            <h4 class="card-header-title">Audience Details</h4>
          </div>
          <div class="card-body">
            <div class="mb-3">
              <label class="form-label">Audience Name</label>
              <p class="form-control-plaintext fw-bold">{{ audience.audience_name }}</p>
            </div>

            <div class="mb-3">
              <label class="form-label">Data Environment</label>
              <p class="form-control-plaintext">{{ audience.audsum_data_environment || '-' }}</p>
            </div>

            <div class="mb-3">
              <label class="form-label">Dataset</label>
              <p class="form-control-plaintext">{{ audience.audsum_dataset || '-' }}</p>
            </div>

            <div class="mb-3">
              <label class="form-label">Created By</label>
              <p class="form-control-plaintext">{{ audience.created_by || '-' }}</p>
            </div>

            <div class="mb-3">
              <label class="form-label">Created Date</label>
              <p class="form-control-plaintext">{{ formatDate(audience.created_date) }}</p>
            </div>

            <div class="mb-3">
              <label class="form-label">Updated Date</label>
              <p class="form-control-plaintext">{{ formatDate(audience.updated_date) }}</p>
            </div>
          </div>
        </div>
      </div>

      <!-- Right Panel - Tabs -->
      <div class="col-md-8">
        <!-- Error Alert -->
        <div v-if="error" class="alert alert-danger alert-dismissible fade show">
          {{ error }}
          <button type="button" class="btn-close" @click="error = null"></button>
        </div>

        <!-- Success Alert -->
        <div v-if="successMessage" class="alert alert-success alert-dismissible fade show">
          {{ successMessage }}
          <button type="button" class="btn-close" @click="successMessage = null"></button>
        </div>

        <!-- Tabs Navigation -->
        <ul class="nav nav-tabs" role="tablist">
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: activeTab === 'criteria' }"
              @click="activeTab = 'criteria'"
              href="#"
            >
              <i class="fa fa-filter me-1" />
              Criteria
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: activeTab === 'distributions' }"
              @click="activeTab = 'distributions'"
              href="#"
            >
              <i class="fa fa-share-nodes me-1" />
              Distributions
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: activeTab === 'history' }"
              @click="activeTab = 'history'"
              href="#"
            >
              <i class="fa fa-clock-rotate-left me-1" />
              Execution History
            </a>
          </li>
          <li class="nav-item">
            <a
              class="nav-link"
              :class="{ active: activeTab === 'exportHistory' }"
              @click="activeTab = 'exportHistory'"
              href="#"
            >
              <i class="fa fa-file-export me-1" />
              Export History
            </a>
          </li>
        </ul>

        <!-- Tab Content -->
        <div class="tab-content">
          <!-- Criteria Tab -->
          <div v-if="activeTab === 'criteria'" class="card border-top-0 rounded-top-0">
            <div class="card-body">
              <!-- Aggregate Audience View -->
              <div v-if="isAggregateAudience">
                <h5 class="mb-3">
                  <i class="fa fa-layer-group me-2 text-primary"></i>
                  Aggregate Audience
                </h5>
                <p class="text-muted">This audience is composed of multiple parent audiences.</p>
                <div class="mb-3">
                  <label class="form-label">Parent Audiences</label>
                  <p>{{ audience.parent_audiences }}</p>
                </div>
              </div>

              <!-- Criteria Builder -->
              <div v-else>
                <!-- Mode Toggle -->
                <div class="btn-group w-100 mb-3" role="group">
                  <button
                    type="button"
                    class="btn"
                    :class="{ 'btn-primary': criterionMode === 'simple', 'btn-outline-primary': criterionMode !== 'simple' }"
                    @click="switchMode('simple')"
                  >
                    <i class="fa fa-list me-2"></i>
                    Simple
                  </button>
                  <button
                    type="button"
                    class="btn"
                    :class="{ 'btn-primary': criterionMode === 'advanced', 'btn-outline-primary': criterionMode !== 'advanced' }"
                    @click="switchMode('advanced')"
                  >
                    <i class="fa fa-filter me-2"></i>
                    Advanced
                  </button>
                  <button
                    type="button"
                    class="btn"
                    :class="{ 'btn-primary': criterionMode === 'json', 'btn-outline-primary': criterionMode !== 'json' }"
                    @click="switchMode('json')"
                  >
                    <i class="fa fa-code me-2"></i>
                    JSON
                  </button>
                </div>

                <!-- Loading Schema -->
                <div v-if="loadingSchema" class="text-center py-4">
                  <div class="spinner-border text-primary" role="status"></div>
                  <p class="mt-2 text-muted">Loading schema...</p>
                </div>

                <!-- Simple Mode -->
                <div v-else-if="criterionMode === 'simple'">
                  <SimplifiedCriterion
                    :criteria="simpleCriteria"
                    :columns="availableColumns"
                    :column-values="columnValues"
                    :loading-values="loadingValues"
                    :is-view-only="false"
                    @update:criteria="simpleCriteria = $event"
                    @columnSelected="onColumnSelected"
                  />
                </div>

                <!-- Advanced Mode -->
                <div v-else-if="criterionMode === 'advanced'">
                  <div class="mb-3 p-3 bg-light rounded">
                    <div class="form-check form-switch mb-2">
                      <input
                        class="form-check-input"
                        type="checkbox"
                        id="negateSwitch"
                        v-model="advancedOptions.negated"
                      />
                      <label class="form-check-label" for="negateSwitch">
                        <i class="fa fa-ban me-2"></i>
                        Generate count NOT matching criterion
                      </label>
                    </div>
                    <div>
                      <label class="form-label fw-bold">
                        <i class="fa fa-layer-group me-2"></i>
                        Match Operator
                      </label>
                      <select class="form-select" v-model="advancedOptions.operator">
                        <option value="OR">Match ANY of the criterion groups below</option>
                        <option value="AND">Match ALL of the criterion groups below</option>
                      </select>
                    </div>
                  </div>

                  <div class="criterion-groups-container">
                    <div
                      v-for="(group, groupIndex) in criterionGroups"
                      :key="groupIndex"
                      class="criterion-group-wrapper mb-3"
                    >
                      <CriterionGroup
                        :group-index="groupIndex"
                        :criteria="group.criteria"
                        :group-operator="group.operator"
                        :groups="group.groups || []"
                        :columns="availableColumns"
                        :column-values="columnValues"
                        :loading-values="groupLoadingValues[groupIndex] || {}"
                        :is-view-only="false"
                        @update:criteria="updateGroupCriteria(groupIndex, $event)"
                        @update:groupOperator="updateGroupOperator(groupIndex, $event)"
                        @update:groups="updateGroupNestedGroups(groupIndex, $event)"
                        @columnSelected="onGroupColumnSelected"
                        @deleteGroup="deleteGroup(groupIndex)"
                      />

                      <!-- Group operator divider -->
                      <div v-if="groupIndex < criterionGroups.length - 1" class="group-operator-divider my-3">
                        <div class="operator-badge">
                          <i :class="advancedOptions.operator === 'AND' ? 'fa fa-plus' : 'fa fa-circle'" class="me-2"></i>
                          {{ advancedOptions.operator }}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div class="text-center mt-3">
                    <button type="button" class="btn btn-outline-primary" @click="addGroup">
                      <i class="fa fa-plus me-2"></i>
                      Add Criterion Group
                    </button>
                  </div>
                </div>

                <!-- JSON Mode -->
                <div v-else-if="criterionMode === 'json'">
                  <div class="json-editor-header mb-2 d-flex justify-content-between align-items-center">
                    <small class="text-muted">
                      <i class="fa fa-info-circle me-1"></i>
                      Edit criterion JSON directly
                    </small>
                    <div class="btn-group btn-group-sm">
                      <button type="button" class="btn btn-outline-secondary" @click="formatJson">
                        <i class="fa fa-align-left me-1"></i>
                        Format
                      </button>
                      <button type="button" class="btn btn-outline-primary" @click="validateJson">
                        <i class="fa fa-check me-1"></i>
                        Validate
                      </button>
                    </div>
                  </div>
                  <textarea
                    v-model="jsonEditorValue"
                    class="form-control json-editor"
                    :class="{ 'is-invalid': jsonError }"
                    rows="15"
                    spellcheck="false"
                  ></textarea>
                  <div v-if="jsonError" class="invalid-feedback d-block">
                    {{ jsonError }}
                  </div>
                  <div v-if="jsonSuccess" class="text-success small mt-2">
                    <i class="fa fa-check-circle me-1"></i>
                    JSON is valid
                  </div>
                </div>

                <!-- Criteria Save/Cancel Controls -->
                <div v-if="hasCriteriaChanges" class="criteria-edit-controls mt-4">
                  <div class="d-flex justify-content-between align-items-center">
                    <div class="text-warning">
                      <i class="fa fa-exclamation-triangle me-2"></i>
                      You have unsaved changes to the criteria.
                    </div>
                    <div class="d-flex gap-2">
                      <button
                        type="button"
                        class="btn btn-outline-secondary"
                        @click="discardCriteriaChanges"
                        :disabled="isSavingCriteria"
                      >
                        <i class="fa fa-times me-2"></i>
                        Cancel
                      </button>
                      <button
                        type="button"
                        class="btn btn-primary"
                        @click="saveCriteriaChanges"
                        :disabled="isSavingCriteria"
                      >
                        <span v-if="isSavingCriteria" class="spinner-border spinner-border-sm me-2"></span>
                        <i v-else class="fa fa-save me-2"></i>
                        Save Changes
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Distributions Tab -->
          <div v-if="activeTab === 'distributions'" class="card border-top-0 rounded-top-0">
            <div class="card-header border-top-0">
              <h4 class="card-header-title">Audience Distributions</h4>
              <span class="float-end">
                <button
                  type="button"
                  class="btn btn-primary btn-sm"
                  @click="showAddDistributionModal"
                >
                  <i class="fa fa-plus me-1" />
                  Add Distribution
                </button>
              </span>
            </div>
            <div class="card-body p-0">
              <Datatable
                v-if="distributions.length > 0"
                tableId="audienceDistributions"
                :columns="distributionColumns"
                uniqueField="id"
                :data="distributions"
                :lengthChange="false"
                :info="false"
                :showLoading="false"
                :ignoreCache="true"
                v-on:selected="onDistributionSelect"
                :compact="true"
              />
              <div v-else class="text-center py-4 text-muted">
                No distributions configured. Click "Add Distribution" to create one.
              </div>
            </div>
          </div>

          <!-- Execution History Tab -->
          <div v-if="activeTab === 'history'" class="card border-top-0 rounded-top-0">
            <div class="card-header border-top-0">
              <h4 class="card-header-title">Execution History</h4>
              <span class="float-end">
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm"
                  @click="loadExecutionHistory"
                  :disabled="loadingHistory"
                >
                  <span v-if="loadingHistory" class="spinner-border spinner-border-sm me-1" style="width: 1em; height: 1em;"/>
                  <i v-else class="fa fa-refresh me-1" />
                  Refresh
                </button>
              </span>
            </div>
            <div class="card-body p-0">
              <Datatable
                v-if="executionHistory.length > 0"
                tableId="executionHistory"
                :columns="executionColumns"
                :formatColumn="executionFormatColumns"
                :ignoreCache="true"
                uniqueField="executionId"
                :data="executionHistory"
                :lengthChange="false"
                :info="false"
                :showLoading="loadingHistory"
                v-on:selected="onExecutionSelect"
                :compact="true"
              />
              <div v-else class="text-center py-4 text-muted">
                No execution history available.
              </div>
            </div>
          </div>

          <!-- Export History Tab -->
          <div v-if="activeTab === 'exportHistory'" class="card border-top-0 rounded-top-0">
            <div class="card-header border-top-0">
              <h4 class="card-header-title">Export History</h4>
              <span class="float-end">
                <button
                  type="button"
                  class="btn btn-outline-secondary btn-sm"
                  @click="loadExportHistory"
                  :disabled="loadingExportHistory"
                >
                  <span v-if="loadingExportHistory" class="spinner-border spinner-border-sm me-1" style="width: 1em; height: 1em;"/>
                  <i v-else class="fa fa-refresh me-1" />
                  Refresh
                </button>
              </span>
            </div>
            <div class="card-body p-0">
              <Datatable
                v-if="exportHistory.length > 0"
                tableId="exportHistory"
                :columns="exportColumns"
                :formatColumn="exportFormatColumns"
                :ignoreCache="true"
                uniqueField="id"
                :data="exportHistory"
                :lengthChange="false"
                :info="false"
                :showLoading="loadingExportHistory"
                v-on:selected="onExportSelect"
                :compact="true"
              />
              <div v-else class="text-center py-4 text-muted">
                No export history available.
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Not Found -->
    <div v-else class="alert alert-warning">
      Audience not found.
    </div>
  </div>
</template>

<script>
import DetailHeader from '../../components/DetailHeader.vue'
import Datatable from '../../components/Datatable.vue'
import SimplifiedCriterion from '../../components/audience/SimplifiedCriterion.vue'
import CriterionGroup from '../../components/audience/CriterionGroup.vue'
import { mapActions } from 'vuex'
import { DateTimeFormat } from '../../utils/commonFunction'
import {
  parseSimpleCriteria,
  parseAdvancedCriteria,
  isSimpleCriterion,
  getCriterionNestingDepth,
  buildSimpleCriterionJson,
  buildAdvancedCriterionJson,
  prePopulateColumnValues,
  prePopulateColumnValuesFromGroups,
  extractValuesFromHistogram,
  createEmptyCriterionGroup,
} from '../../utils/criterionUtils'

export default {
  name: 'AudienceDetail',
  components: { DetailHeader, Datatable, SimplifiedCriterion, CriterionGroup },
  data() {
    return {
      loading: true,
      loadingSchema: false,
      activeTab: 'criteria',
      error: null,
      successMessage: null,
      loadingHistory: false,
      loadingExportHistory: false,
      isSavingCriteria: false,
      // Criteria builder state
      criterionMode: 'simple',
      availableColumns: [],
      columnValues: {},
      loadingValues: {},
      simpleCriteria: [{ column: null, values: [] }],
      advancedOptions: {
        operator: 'OR',
        negated: false,
      },
      criterionGroups: [{
        operator: 'AND',
        criteria: [{
          column: null,
          condition: 'IN',
          values: [],
          lowerbound: null,
          upperbound: null,
          singleValue: null,
        }],
        groups: [],
      }],
      groupLoadingValues: {},
      jsonEditorValue: '',
      jsonError: null,
      jsonSuccess: false,
      originalCriteriaJson: '',
      // Store original criteria state for change detection
      originalSimpleCriteria: null,
      originalAdvancedOptions: null,
      originalCriterionGroups: null,
      originalCriterionMode: 'simple',
      distributionColumns: [
        { data: 'distribution_name', title: 'Name' },
        { data: 'schedule', title: 'Schedule' },
        { data: 'start_date', title: 'Start Date' },
        { data: 'end_date', title: 'End Date' },
      ],
      executionColumns: [
        { data: 'id', title: 'Execution ID' },
        { 
          data: 'status', 
          title: 'Status',
          render: function (data, type, row) {
            if (!data) return '';
            const status = data.toLowerCase();
            let statusStr = data;
            if (status === 'succeeded' || status === 'completed' || status === 'success') {
              statusStr = "<span class='text-success'>●</span> " + data;
            } else if (status === 'failed' || status === 'error') {
              statusStr = "<span class='text-danger'>●</span> " + data;
            } else if (status === 'running' || status === 'in_progress') {
              statusStr = "<span class='text-primary'>●</span> " + data;
            } else if (status === 'submitted' || status === 'pending' || status === 'queued') {
              statusStr = "<span class='text-warning'>●</span> " + data;
            } else if (status === 'cancelled') {
              statusStr = "<span class='text-secondary'>●</span> " + data;
            }
            return statusStr;
          }
        },
        { data: 'statistics.submission_time', title: 'Submitted At' },
        { data: 'statistics.completion_time', title: 'Completed At' },
      ],
      executionFormatColumns: [
        { index: 2, type: 'datetime' }, // Submitted At
        { index: 3, type: 'datetime' }, // Completed At
      ],
      exportColumns: [
        { data: 'audsum_execution_id', title: 'Execution ID' },
        { data: 'count', title: 'Count', render: function (data) {
          if (data == null) return '-'
          return typeof data === 'number' ? data.toLocaleString() : data
        }},
        { data: 'output_location', title: 'Output Location', render: function (data) {
          if (!data || data === '' || data.trim() === '') return '-'
          return data
        }},
        { data: 'executed_time', title: 'Executed Time' },
        { data: 'executed_by', title: 'Executed By' },
      ],
      exportFormatColumns: [
        { index: 3, type: 'datetime' }, // Executed Time
      ],
    }
  },
  computed: {
    audienceName() {
      return this.$route.params.name
    },
    audience() {
      return this.$store.state.audience.currentAudience
    },
    distributions() {
      return this.$store.state.audience.distributions || []
    },
    executionHistory() {
      return this.$store.state.audience.executionHistory || []
    },
    exportHistory() {
      return this.$store.state.audience.exportHistory || []
    },
    audienceCount() {
      return this.$store.state.audience.audienceCount
    },
    isLoadingCount() {
      return this.$store.state.audience.isLoadingCount
    },
    isAggregateAudience() {
      return this.audience && this.audience.parent_audiences && this.audience.parent_audiences !== ''
    },
    hasCriteriaChanges() {
      if (!this.originalSimpleCriteria) return false
      const currentJson = this.buildCriterionJson()
      return currentJson !== this.originalCriteriaJson
    },
  },
  methods: {
    ...mapActions({
      showModal: 'showModal',
      fetchAudienceByName: 'audience/fetchAudienceByName',
      fetchDistributions: 'audience/fetchDistributions',
      fetchExecutionHistory: 'audience/fetchExecutionHistory',
      fetchExportHistory: 'audience/fetchExportHistory',
      fetchSchema: 'audience/fetchSchema',
      fetchColumnValues: 'audience/fetchColumnValues',
      updateAudience: 'audience/updateAudience',
      deleteAudience: 'audience/deleteAudience',
      runCountQuery: 'audience/runCountQuery',
    }),
    async loadAudience() {
      this.loading = true
      try {
        await this.fetchAudienceByName(this.audienceName)
        if (this.audience) {
          await this.fetchDistributions(this.audience.audience_name)
          this.parseCriteriaFromAudience()
          await this.loadSchema()
        }
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to load audience'
      } finally {
        this.loading = false
      }
    },
    async loadSchema() {
      if (!this.audience || !this.audience.audsum_data_environment || !this.audience.audsum_dataset) return

      this.loadingSchema = true
      try {
        const schemaResult = await this.fetchSchema({
          dataEnvironmentId: this.audience.audsum_data_environment,
          dataset: this.audience.audsum_dataset
        })
        const columns = schemaResult?.schema?.columns || schemaResult?.columns || []
        this.availableColumns = columns.map(c => c.name || c)
      } catch (error) {
        console.error('Failed to load schema:', error)
      } finally {
        this.loadingSchema = false
      }
    },
    parseCriteriaFromAudience() {
      if (!this.audience) return

      try {
        const json = this.audience.audience_criterion_json || '{}'
        this.originalCriteriaJson = json
        this.jsonEditorValue = JSON.stringify(JSON.parse(json), null, 2)

        const query = JSON.parse(json)
        if (!query || !query.criterion) {
          this.simpleCriteria = [{ column: null, values: [] }]
          this.storeOriginalCriteriaState()
          return
        }

        const criterion = query.criterion

        // Check for negation
        let actualCriterion = criterion
        if (criterion.operator === 'not' && criterion.operand) {
          this.advancedOptions.negated = true
          actualCriterion = criterion.operand
        } else {
          this.advancedOptions.negated = false
        }

        // Check if it's a simple AND criterion
        if (isSimpleCriterion(actualCriterion)) {
          this.criterionMode = 'simple'
          this.simpleCriteria = parseSimpleCriteria(actualCriterion)
          // Pre-populate columnValues with existing values so they show in the multiselect
          this.columnValues = prePopulateColumnValues(this.simpleCriteria, this.columnValues)
        } else {
          this.criterionMode = 'advanced'
          const parsed = parseAdvancedCriteria(actualCriterion)
          this.advancedOptions.operator = parsed.operator
          this.criterionGroups = parsed.groups
          // Pre-populate columnValues for advanced mode criteria
          this.columnValues = prePopulateColumnValuesFromGroups(this.criterionGroups, this.columnValues)
        }

        // Store the original state for change detection
        this.storeOriginalCriteriaState()
      } catch (e) {
        console.error('Failed to parse criteria:', e)
        this.criterionMode = 'json'
        this.storeOriginalCriteriaState()
      }
    },
    storeOriginalCriteriaState() {
      // Deep clone the current state as the baseline for change detection
      this.originalSimpleCriteria = JSON.parse(JSON.stringify(this.simpleCriteria))
      this.originalAdvancedOptions = JSON.parse(JSON.stringify(this.advancedOptions))
      this.originalCriterionGroups = JSON.parse(JSON.stringify(this.criterionGroups))
      this.originalCriterionMode = this.criterionMode
      this.originalCriteriaJson = this.buildCriterionJson()
    },
    switchMode(mode) {
      // When switching TO JSON mode, sync current criteria to JSON
      if (mode === 'json' && this.criterionMode !== 'json') {
        this.syncToJson()
      }
      // When switching FROM JSON mode to Simple or Advanced, parse JSON and update criteria
      if (this.criterionMode === 'json' && mode !== 'json') {
        this.applyJsonToCriteria(mode)
      }
      this.criterionMode = mode
      this.jsonError = null
      this.jsonSuccess = false
    },
    applyJsonToCriteria(targetMode) {
      try {
        const parsed = JSON.parse(this.jsonEditorValue)
        if (!parsed || !parsed.criterion) {
          console.warn('No criterion found in JSON')
          return
        }

        const criterion = parsed.criterion

        // Check for negation
        let actualCriterion = criterion
        if (criterion.operator === 'not' && criterion.operand) {
          this.advancedOptions.negated = true
          actualCriterion = criterion.operand
        } else {
          this.advancedOptions.negated = false
        }

        if (targetMode === 'simple') {
          // Parse as simple criteria - flatten all nested criteria
          const newCriteria = parseSimpleCriteria(actualCriterion)
          // Pre-populate columnValues BEFORE updating simpleCriteria
          this.columnValues = prePopulateColumnValues(newCriteria, this.columnValues)
          this.simpleCriteria = newCriteria
        } else if (targetMode === 'advanced') {
          // Check nesting depth - if > 2, warn that structure will be simplified
          const depth = getCriterionNestingDepth(actualCriterion)
          if (depth > 2) {
            console.warn(`Complex nested criterion (depth: ${depth}) will be simplified for Advanced view`)
          }
          // Parse as advanced criteria - will flatten deeply nested structures
          const parsedAdvanced = parseAdvancedCriteria(actualCriterion)
          this.advancedOptions.operator = parsedAdvanced.operator
          this.criterionGroups = parsedAdvanced.groups
          this.columnValues = prePopulateColumnValuesFromGroups(this.criterionGroups, this.columnValues)
        }
      } catch (e) {
        console.error('Failed to parse JSON for mode switch:', e)
        this.jsonError = 'Invalid JSON: ' + e.message
      }
    },
    syncToJson() {
      this.jsonEditorValue = this.buildCriterionJson()
      try {
        this.jsonEditorValue = JSON.stringify(JSON.parse(this.jsonEditorValue), null, 2)
      } catch (e) {
        // Keep as-is if invalid
      }
    },
    buildCriterionJson() {
      if (this.criterionMode === 'json') {
        return this.jsonEditorValue
      }

      if (this.criterionMode === 'simple') {
        return JSON.stringify(buildSimpleCriterionJson(this.simpleCriteria))
      } else {
        return JSON.stringify(buildAdvancedCriterionJson(this.criterionGroups, {
          operator: this.advancedOptions.operator,
          negated: this.advancedOptions.negated
        }))
      }
    },
    formatJson() {
      try {
        const parsed = JSON.parse(this.jsonEditorValue)
        this.jsonEditorValue = JSON.stringify(parsed, null, 2)
        this.jsonError = null
      } catch (e) {
        this.jsonError = 'Invalid JSON: ' + e.message
      }
    },
    validateJson() {
      this.jsonError = null
      this.jsonSuccess = false
      try {
        const parsed = JSON.parse(this.jsonEditorValue)
        if (!parsed.criterion) {
          this.jsonError = 'JSON must contain a "criterion" property'
          return
        }
        this.jsonSuccess = true
        setTimeout(() => { this.jsonSuccess = false }, 3000)
      } catch (e) {
        this.jsonError = 'Invalid JSON: ' + e.message
      }
    },
    updateGroupCriteria(groupIndex, criteria) {
      // Create new object reference with deep cloned criteria to ensure reactivity
      this.criterionGroups[groupIndex] = {
        ...this.criterionGroups[groupIndex],
        criteria: JSON.parse(JSON.stringify(criteria))
      }
      // Force reactivity by creating a new array reference
      this.criterionGroups = [...this.criterionGroups]
    },
    updateGroupOperator(groupIndex, operator) {
      this.criterionGroups[groupIndex].operator = operator
    },
    updateGroupNestedGroups(groupIndex, groups) {
      // Ensure groups array exists
      if (!this.criterionGroups[groupIndex].groups) {
        this.criterionGroups[groupIndex].groups = []
      }
      // Deep clone and create new object reference to ensure reactivity
      this.criterionGroups[groupIndex] = {
        ...this.criterionGroups[groupIndex],
        groups: JSON.parse(JSON.stringify(groups))
      }
      // Force reactivity by creating a new array reference
      this.criterionGroups = [...this.criterionGroups]
    },
    addGroup() {
      this.criterionGroups.push(createEmptyCriterionGroup())
    },
    deleteGroup(groupIndex) {
      if (this.criterionGroups.length > 1) {
        this.criterionGroups.splice(groupIndex, 1)
      }
    },
    async onColumnSelected({ column, index }) {
      if (!column || this.columnValues[column]) return

      this.loadingValues = { ...this.loadingValues, [index]: true }
      try {
        const result = await this.fetchColumnValues({
          dataEnvironmentId: this.audience.audsum_data_environment,
          dataset: this.audience.audsum_dataset,
          column
        })
        const values = extractValuesFromHistogram(result)
        this.columnValues = { ...this.columnValues, [column]: values }
      } catch (error) {
        console.error('Failed to load column values:', error)
        this.columnValues = { ...this.columnValues, [column]: [] }
      } finally {
        this.loadingValues = { ...this.loadingValues, [index]: false }
      }
    },
    async onGroupColumnSelected({ column, groupIndex, criterionIndex }) {
      if (!column || this.columnValues[column]) return

      // Update groupLoadingValues reactively
      const newGroupLoadingValues = { ...this.groupLoadingValues }
      if (!newGroupLoadingValues[groupIndex]) {
        newGroupLoadingValues[groupIndex] = {}
      }
      newGroupLoadingValues[groupIndex] = { ...newGroupLoadingValues[groupIndex], [criterionIndex]: true }
      this.groupLoadingValues = newGroupLoadingValues

      try {
        const result = await this.fetchColumnValues({
          dataEnvironmentId: this.audience.audsum_data_environment,
          dataset: this.audience.audsum_dataset,
          column
        })
        const values = extractValuesFromHistogram(result)
        this.columnValues = { ...this.columnValues, [column]: values }
      } catch (error) {
        console.error('Failed to load column values:', error)
        this.columnValues = { ...this.columnValues, [column]: [] }
      } finally {
        const finalGroupLoadingValues = { ...this.groupLoadingValues }
        finalGroupLoadingValues[groupIndex] = { ...finalGroupLoadingValues[groupIndex], [criterionIndex]: false }
        this.groupLoadingValues = finalGroupLoadingValues
      }
    },
    async loadExecutionHistory() {
      if (!this.audience) return
      this.loadingHistory = true
      try {
        await this.fetchExecutionHistory({
          dataEnvironmentId: this.audience.audsum_data_environment,
          dataset: this.audience.audsum_dataset
        })
      } catch (error) {
        console.error('Failed to load execution history:', error)
      } finally {
        this.loadingHistory = false
      }
    },
    async loadExportHistory() {
      if (!this.audience) return
      this.loadingExportHistory = true
      try {
        await this.fetchExportHistory({
          audienceName: this.audience.audience_name
        })
      } catch (error) {
        console.error('Failed to load export history:', error)
      } finally {
        this.loadingExportHistory = false
      }
    },
    onExportSelect(exportItem) {
      this.showModal({
        component: 'ExportUserIdsDetail',
        data: {
          audience: this.audience,
          execution: exportItem,
          dataEnvironmentId: this.audience.audsum_data_environment,
          dataset: this.audience.audsum_dataset,
          screensize: "xl"
        }
      })
    },
    discardCriteriaChanges() {
      // Restore the original criteria state
      if (this.originalSimpleCriteria) {
        this.simpleCriteria = JSON.parse(JSON.stringify(this.originalSimpleCriteria))
      }
      if (this.originalAdvancedOptions) {
        this.advancedOptions = JSON.parse(JSON.stringify(this.originalAdvancedOptions))
      }
      if (this.originalCriterionGroups) {
        this.criterionGroups = JSON.parse(JSON.stringify(this.originalCriterionGroups))
      }
      if (this.originalCriterionMode) {
        this.criterionMode = this.originalCriterionMode
      }
      // Restore JSON editor
      try {
        const json = this.audience?.audience_criterion_json || '{}'
        this.jsonEditorValue = JSON.stringify(JSON.parse(json), null, 2)
      } catch (e) {
        this.jsonEditorValue = this.audience?.audience_criterion_json || '{}'
      }
      this.jsonError = null
      this.jsonSuccess = false
    },
    async saveCriteriaChanges() {
      if (!this.audience) return

      this.isSavingCriteria = true
      this.error = null

      try {
        // Build the updated audience data with new criteria
        const updatedAudience = {
          ...this.audience,
          audience_criterion_json: this.buildCriterionJson()
        }

        await this.updateAudience({
          audienceName: this.audience.audience_name,
          audienceData: updatedAudience
        })
        this.successMessage = 'Criteria saved successfully'
        // Reload the audience to get the updated data and reset original state
        await this.loadAudience()
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to save criteria'
      } finally {
        this.isSavingCriteria = false
      }
    },
    onDeleteClick() {
      const self = this
      this.showModal({
        component: 'Confirm',
        data: {
          title: 'Delete Audience',
          message: `Are you sure you want to delete "${this.audience.audience_name}"? This action cannot be undone.`,
          async confirm() {
            try {
              await self.deleteAudience(self.audience.audience_name)
              self.$store.dispatch('addToast', {
                type: 'success',
                message: 'Audience deleted successfully'
              })
              self.$router.push(`/${self.$store.state.currentClient.cid}/amdp/audiences`)
            } catch (error) {
              self.error = error.response?.data?.message || error.message || 'Failed to delete audience'
            }
          }
        }
      })
    },
    onCloseClick() {
      this.$router.push(`/${this.$store.state.currentClient.cid}/amdp/audiences/list`)
    },
    async onRunCount() {
      if (!this.audience) return
      this.error = null

      try {
        // Use buildCriterionJson() to get the current criteria from the UI
        // This ensures we use the current state, including any edits in Advanced mode
        const criterionJson = this.buildCriterionJson()
        const parsed = JSON.parse(criterionJson)
        const criterion = parsed.criterion || parsed
        
        const queryBody = {
          dataset: this.audience.audsum_dataset,
          criterion: criterion
        }

        await this.runCountQuery({
          dataEnvironmentId: this.audience.audsum_data_environment,
          queryBody
        })
      } catch (error) {
        this.error = error.response?.data?.message || error.message || 'Failed to get audience count'
      }
    },
    showHistogramModal() {
      this.showModal({
        component: 'RunHistogram',
        data: {
          audience: this.audience,
          columns: this.availableColumns,
          criterion: this.buildCriterionJson()
        }
      })
    },
    showTopIndexingModal() {
      this.showModal({
        component: 'TopIndexing',
        data: {
          audience: this.audience,
          columns: this.availableColumns,
          criterion: this.buildCriterionJson()
        }
      })
    },
    showExportModal() {
      this.showModal({
        component: 'ExportUserIds',
        data: {
          audience: this.audience,
          columns: this.availableColumns,
          screensize: 'xl'
        }
      })
    },
    showAddDistributionModal() {
      this.showModal({
        component: 'AddDistribution',
        data: {
          audienceName: this.audience.audience_name
        }
      })
    },
    onDistributionSelect(distribution) {
      this.showModal({
        component: 'DistributionDetail',
        data: {
          audienceName: this.audience.audience_name,
          distribution: distribution,
          screensize: 'xl'
        }
      })
    },
    onExecutionSelect(execution) {
      this.showModal({
        component: 'ExecutionDetail',
        data: {
          audience: this.audience,
          execution: execution,
          dataEnvironmentId: this.audience.audsum_data_environment,
          dataset: this.audience.audsum_dataset,
          screensize: 'xl'
        }
      })
    },
    formatDate(dateString) {
      return DateTimeFormat(dateString)
    },
    formatNumber(num) {
      if (num == null) return '-'
      return num.toLocaleString()
    },
  },
  mounted() {
    this.loadAudience()
  },
  watch: {
    audienceName(newName) {
      if (newName == null || newName === undefined) return
      this.loadAudience()
    },
    activeTab(newTab) {
      if (newTab === 'history' && this.executionHistory.length === 0) {
        this.loadExecutionHistory()
      }
      if (newTab === 'exportHistory' && this.exportHistory.length === 0) {
        this.loadExportHistory()
      }
    }
  }
}
</script>

<style scoped>
.form-label {
  font-size: 0.75rem;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6E84A3;
}

.nav-tabs .nav-link {
  cursor: pointer;
}

.rounded-top-0 {
  border-top-left-radius: 0 !important;
  border-top-right-radius: 0 !important;
}

.json-editor {
  font-family: 'Courier New', Courier, monospace;
  font-size: 0.875rem;
  line-height: 1.5;
  background-color: #1e1e1e;
  color: #d4d4d4;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  resize: vertical;
  tab-size: 2;
}

.json-editor:focus {
  outline: none;
  border-color: var(--bs-primary);
  box-shadow: 0 0 0 0.2rem rgba(13, 110, 253, 0.25);
}

.json-editor.is-invalid {
  border-color: var(--bs-danger);
}

.criteria-edit-controls {
  padding: 1rem 1.25rem;
  background: linear-gradient(135deg, #fff3cd, #ffeeba);
  border: 1px solid #ffc107;
  border-radius: 0.5rem;
  box-shadow: 0 2px 8px rgba(255, 193, 7, 0.2);
}

.criteria-edit-controls .btn {
  font-weight: 500;
  transition: all 0.2s ease;
}

.criteria-edit-controls .btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
}

.group-operator-divider {
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
}

.group-operator-divider::before {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 50%;
  height: 2px;
  background: linear-gradient(to right, transparent, #dee2e6 20%, #dee2e6 80%, transparent);
}

.operator-badge {
  background: linear-gradient(135deg, var(--bs-primary, #2C7BE5), #0056b3);
  color: white;
  padding: 0.5rem 1.5rem;
  border-radius: 2rem;
  font-weight: 600;
  font-size: 0.875rem;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(13, 110, 253, 0.3);
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
}

.btn-group .btn {
  flex: 1;
}
</style>
